import { RefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { orbitScrollContainerOf, scrollOrbitThreadToEnd } from './threadScroll';

/** How far above the end a reader still counts as at the end, and is kept there as it grows. */
export const ORBIT_FOLLOW_THRESHOLD_PX = 80;

const isAtEnd = (container: HTMLElement): boolean =>
  container.scrollHeight - container.scrollTop - container.clientHeight <=
  ORBIT_FOLLOW_THRESHOLD_PX;

export interface IOrbitThreadFollow {
  enabled: boolean;
  /** The list draws nothing while it has nothing to show; its refs are empty until it does. */
  mounted: boolean;
  /** Any node inside the thread's scroll container. */
  endRef: RefObject<HTMLElement>;
  /** The thread's content, watched for growth after render. */
  contentRef: RefObject<HTMLElement>;
  sessionId: string | null;
  hasTurns: boolean;
  /** The question on its way to the server, if any. */
  sending: string | null;
}

/**
 * The full page's thread, as chat products scroll it: an opened chat lands on its newest turn once
 * its turns are on screen, a sent question brings the end into view, and while an answer grows
 * (progress, the answer, then charts and tables that size themselves after render) the end is
 * followed only for a reader already at it. Every move goes through the panel's
 * `scrollOrbitThreadToEnd`.
 */
const useOrbitThreadFollow = ({
  enabled,
  mounted,
  endRef,
  contentRef,
  sessionId,
  hasTurns,
  sending
}: IOrbitThreadFollow): void => {
  const following = useRef(true);
  const landedOn = useRef<string | null>();

  const jumpToEnd = useCallback(() => {
    following.current = true;
    scrollOrbitThreadToEnd(endRef.current);
  }, [endRef]);

  useLayoutEffect(() => {
    if (enabled && hasTurns && landedOn.current !== sessionId) {
      landedOn.current = sessionId;
      jumpToEnd();
    }
  }, [enabled, hasTurns, sessionId, jumpToEnd]);

  useLayoutEffect(() => {
    if (enabled && sending !== null) {
      jumpToEnd();
    }
  }, [enabled, sending, jumpToEnd]);

  useEffect(() => {
    const container = orbitScrollContainerOf(endRef.current);
    const content = contentRef.current;

    if (!enabled || !mounted || container === null || content === null) {
      return undefined;
    }

    const onScroll = () => {
      following.current = isAtEnd(container);
    };
    // The container too: a composer growing a line shrinks it.
    const observer = new ResizeObserver(() => {
      if (following.current) {
        scrollOrbitThreadToEnd(container);
      }
    });

    container.addEventListener('scroll', onScroll, { passive: true });
    observer.observe(content);
    observer.observe(container);

    return () => {
      container.removeEventListener('scroll', onScroll);
      observer.disconnect();
    };
  }, [enabled, mounted, endRef, contentRef]);
};

export default useOrbitThreadFollow;
